import type { SuggestedConfigItem, ConfigCategory } from '../types';

interface ParseResult {
  suggestedItems: SuggestedConfigItem[];
}

/**
 * Parses a Change Order's scope changes and suggests configuration items
 * This uses keyword matching and pattern recognition to identify common config needs
 */
export function parseChangeOrderForConfigItems(
  scopeChanges: string,
  changeOrderDescription: string
): ParseResult {
  const suggestedItems: SuggestedConfigItem[] = [];
  const text = `${changeOrderDescription} ${scopeChanges}`.toLowerCase();

  // SLA Detection
  if (
    text.includes('sla') ||
    text.includes('service level') ||
    text.includes('response time') ||
    text.includes('resolution time') ||
    text.includes('turnaround time')
  ) {
    suggestedItems.push({
      id: `suggested-${Date.now()}-sla`,
      category: 'sla',
      name: 'SLA Goals',
      description: 'Service Level Agreement configuration for response and resolution times',
      reasoning: 'Change order mentions SLA, response time, or resolution time requirements',
      accepted: false,
      createdAt: new Date().toISOString(),
    });
  }

  // Queue Detection
  if (
    text.includes('queue') ||
    text.includes('triage') ||
    text.includes('workload') ||
    text.includes('assignment group')
  ) {
    suggestedItems.push({
      id: `suggested-${Date.now()}-queue`,
      category: 'queue',
      name: 'Queue Configuration',
      description: 'Custom queues for organizing and triaging requests',
      reasoning: 'Change order mentions queues, triage, or workload management',
      accepted: false,
      createdAt: new Date().toISOString(),
    });
  }

  // Approval Detection
  if (
    text.includes('approval') ||
    text.includes('approve') ||
    text.includes('authorization') ||
    text.includes('sign-off') ||
    text.includes('manager review')
  ) {
    suggestedItems.push({
      id: `suggested-${Date.now()}-approval`,
      category: 'approval',
      name: 'Approval Workflow',
      description: 'Approval process configuration for request types',
      reasoning: 'Change order mentions approval, authorization, or manager review requirements',
      accepted: false,
      createdAt: new Date().toISOString(),
    });
  }

  // Asset Management Detection
  if (
    text.includes('asset') ||
    text.includes('cmdb') ||
    text.includes('configuration item') ||
    text.includes('inventory') ||
    text.includes('hardware') ||
    text.includes('software license')
  ) {
    suggestedItems.push({
      id: `suggested-${Date.now()}-asset`,
      category: 'asset',
      name: 'Asset Management',
      description: 'Asset/CMDB schema and import configuration',
      reasoning: 'Change order mentions assets, CMDB, inventory, or configuration items',
      accepted: false,
      createdAt: new Date().toISOString(),
    });
  }

  // Automation Detection
  if (
    text.includes('automat') ||
    text.includes('auto-assign') ||
    text.includes('escalat') ||
    text.includes('routing') ||
    text.includes('trigger')
  ) {
    suggestedItems.push({
      id: `suggested-${Date.now()}-automation`,
      category: 'automation',
      name: 'Automation Rules',
      description: 'Automated workflows for assignment, escalation, and transitions',
      reasoning: 'Change order mentions automation, auto-assignment, escalation, or triggers',
      accepted: false,
      createdAt: new Date().toISOString(),
    });
  }

  // Notification/Email Detection
  if (
    text.includes('notification') ||
    text.includes('email') ||
    text.includes('alert') ||
    text.includes('notify') ||
    text.includes('communication')
  ) {
    suggestedItems.push({
      id: `suggested-${Date.now()}-notification`,
      category: 'notification',
      name: 'Notification Configuration',
      description: 'Email and notification settings for various events',
      reasoning: 'Change order mentions notifications, emails, or alerts',
      accepted: false,
      createdAt: new Date().toISOString(),
    });
  }

  // Portal/Self-Service Detection
  if (
    text.includes('portal') ||
    text.includes('self-service') ||
    text.includes('customer portal') ||
    text.includes('service desk portal') ||
    text.includes('branding') ||
    text.includes('help center')
  ) {
    suggestedItems.push({
      id: `suggested-${Date.now()}-portal`,
      category: 'portal',
      name: 'Portal Configuration',
      description: 'Portal branding, announcements, and help text',
      reasoning: 'Change order mentions portal, self-service, or branding requirements',
      accepted: false,
      createdAt: new Date().toISOString(),
    });
  }

  // Knowledge Base Detection
  if (
    text.includes('knowledge base') ||
    text.includes('kb ') ||
    text.includes('article') ||
    text.includes('documentation') ||
    text.includes('help article') ||
    text.includes('faq')
  ) {
    suggestedItems.push({
      id: `suggested-${Date.now()}-knowledge-base`,
      category: 'knowledge-base',
      name: 'Knowledge Base',
      description: 'Knowledge base articles and search configuration',
      reasoning: 'Change order mentions knowledge base, articles, or documentation',
      accepted: false,
      createdAt: new Date().toISOString(),
    });
  }

  // Integration Detection
  if (
    text.includes('integrat') ||
    text.includes('slack') ||
    text.includes('teams') ||
    text.includes('microsoft teams') ||
    text.includes('confluence') ||
    text.includes('api') ||
    text.includes('third-party') ||
    text.includes('external system')
  ) {
    suggestedItems.push({
      id: `suggested-${Date.now()}-integration`,
      category: 'integration',
      name: 'Integration Configuration',
      description: 'Third-party integrations (Slack, Teams, Confluence, APIs)',
      reasoning: 'Change order mentions integrations with external systems',
      accepted: false,
      createdAt: new Date().toISOString(),
    });
  }

  // CSAT/Survey Detection
  if (
    text.includes('csat') ||
    text.includes('customer satisfaction') ||
    text.includes('survey') ||
    text.includes('feedback') ||
    text.includes('rating')
  ) {
    suggestedItems.push({
      id: `suggested-${Date.now()}-csat`,
      category: 'csat',
      name: 'CSAT Surveys',
      description: 'Customer satisfaction survey configuration',
      reasoning: 'Change order mentions CSAT, surveys, or customer feedback',
      accepted: false,
      createdAt: new Date().toISOString(),
    });
  }

  // Dashboard/Reporting Detection
  if (
    text.includes('dashboard') ||
    text.includes('report') ||
    text.includes('metric') ||
    text.includes('kpi') ||
    text.includes('analytic') ||
    text.includes('performance')
  ) {
    suggestedItems.push({
      id: `suggested-${Date.now()}-dashboard`,
      category: 'dashboard',
      name: 'Dashboard & Reports',
      description: 'Performance dashboards and custom reports',
      reasoning: 'Change order mentions dashboards, reports, metrics, or analytics',
      accepted: false,
      createdAt: new Date().toISOString(),
    });
  }

  // Service Catalog Detection
  if (
    text.includes('service catalog') ||
    text.includes('service offering') ||
    text.includes('service portfolio')
  ) {
    suggestedItems.push({
      id: `suggested-${Date.now()}-service-catalog`,
      category: 'service-catalog',
      name: 'Service Catalog',
      description: 'Service catalog items and offerings',
      reasoning: 'Change order mentions service catalog or service offerings',
      accepted: false,
      createdAt: new Date().toISOString(),
    });
  }

  return { suggestedItems };
}

/**
 * Extract potential project names from scope changes
 * This is a simple heuristic - in production, you'd use an LLM for better accuracy
 */
export function extractProjectNames(scopeChanges: string): string[] {
  const projects: string[] = [];
  const text = scopeChanges.toLowerCase();

  // Look for common patterns like "Project X", "X project", "X service desk"
  const patterns = [
    /(?:project|service desk|jsm)\s+(?:named|called)?\s*["']?([A-Z][A-Za-z0-9\s]{2,30})["']?/gi,
    /["']([A-Z][A-Za-z0-9\s]{2,30})["']\s+(?:project|service desk|jsm)/gi,
    /create\s+(?:a\s+)?(?:new\s+)?(?:project|service desk|jsm)\s+for\s+([A-Z][A-Za-z0-9\s]{2,30})/gi,
  ];

  patterns.forEach((pattern) => {
    const matches = scopeChanges.matchAll(pattern);
    for (const match of matches) {
      if (match[1] && !projects.includes(match[1].trim())) {
        projects.push(match[1].trim());
      }
    }
  });

  return projects;
}

/**
 * Extract potential team names from scope changes
 */
export function extractTeamNames(scopeChanges: string): string[] {
  const teams: string[] = [];
  const text = scopeChanges.toLowerCase();

  const patterns = [
    /(?:team|group)\s+(?:named|called)?\s*["']?([A-Z][A-Za-z0-9\s]{2,30})["']?/gi,
    /["']([A-Z][A-Za-z0-9\s]{2,30})["']\s+(?:team|group)/gi,
    /create\s+(?:a\s+)?(?:new\s+)?team\s+for\s+([A-Z][A-Za-z0-9\s]{2,30})/gi,
  ];

  patterns.forEach((pattern) => {
    const matches = scopeChanges.matchAll(pattern);
    for (const match of matches) {
      if (match[1] && !teams.includes(match[1].trim())) {
        teams.push(match[1].trim());
      }
    }
  });

  return teams;
}

/**
 * Analyze scope changes to determine if this should be a separate SOW config scope
 */
export function shouldSuggestSeparateScope(scopeChanges: string, description: string): {
  suggest: boolean;
  reason: string;
} {
  const text = `${description} ${scopeChanges}`.toLowerCase();

  // Major scope changes that might warrant separate tracking
  if (
    text.includes('new project') ||
    text.includes('additional project') ||
    text.includes('separate project')
  ) {
    return {
      suggest: true,
      reason: 'Change order includes new project creation',
    };
  }

  if (
    text.includes('phase 2') ||
    text.includes('phase two') ||
    text.includes('next phase')
  ) {
    return {
      suggest: true,
      reason: 'Change order appears to be for a subsequent project phase',
    };
  }

  if (
    text.includes('expansion') ||
    text.includes('additional scope') ||
    text.includes('scope extension')
  ) {
    return {
      suggest: true,
      reason: 'Change order represents significant scope expansion',
    };
  }

  return {
    suggest: false,
    reason: '',
  };
}
